import { Button, Input, Modal } from "antd";
import { useEffect, useState } from "react";

type UnsavedChangesModalProps = {
  isOpen: boolean;
  isSaving: boolean;
  title: string;
  description: string;
  workspaceName: string;
  discardLabel: string;
  saveAsNewLabel: string;
  saveAsNewConfirmLabel: string;
  saveCurrentWorkspaceLabel?: string;
  saveCurrentWorkspaceConfirmLabel?: string;
  saveCurrentWorkspaceWarning?: string;
  applyLatestTemplateLabel?: string;
  onCancel: () => void;
  onDiscard: () => void;
  onSaveAsNew: (copyName: string) => void | Promise<void>;
  onWorkspaceAction?: () => void | Promise<void>;
};

export function UnsavedChangesModal({
  isOpen,
  isSaving,
  title,
  description,
  workspaceName,
  discardLabel,
  saveAsNewLabel,
  saveAsNewConfirmLabel,
  saveCurrentWorkspaceLabel,
  saveCurrentWorkspaceConfirmLabel,
  saveCurrentWorkspaceWarning,
  applyLatestTemplateLabel,
  onCancel,
  onDiscard,
  onSaveAsNew,
  onWorkspaceAction,
}: UnsavedChangesModalProps) {
  const [showCopyNameInput, setShowCopyNameInput] = useState(false);
  const [copyName, setCopyName] = useState("");
  const [showWorkspaceActionConfirm, setShowWorkspaceActionConfirm] =
    useState(false);
  const [activeAction, setActiveAction] = useState<"saveAsNew" | "workspace">(
    "saveAsNew",
  );

  useEffect(() => {
    if (!isOpen) return;
    setShowCopyNameInput(false);
    setCopyName("");
    setShowWorkspaceActionConfirm(false);
    setActiveAction("saveAsNew");
  }, [isOpen]);

  function handleSaveAsNewClick() {
    setActiveAction("saveAsNew");
    setShowWorkspaceActionConfirm(false);

    if (showCopyNameInput) {
      onSaveAsNew(copyName.trim());
      return;
    }

    setCopyName(`${workspaceName} copy`);
    setShowCopyNameInput(true);
  }

  function handleCancel() {
    setShowCopyNameInput(false);
    setCopyName("");
    setShowWorkspaceActionConfirm(false);
    setActiveAction("saveAsNew");
    onCancel();
  }

  function handleDiscard() {
    setShowCopyNameInput(false);
    setCopyName("");
    setShowWorkspaceActionConfirm(false);
    setActiveAction("saveAsNew");
    onDiscard();
  }

  const workspaceActionLabel =
    saveCurrentWorkspaceLabel ?? applyLatestTemplateLabel;
  const workspaceActionConfirmLabel = saveCurrentWorkspaceConfirmLabel;
  const workspaceActionWarning = saveCurrentWorkspaceWarning;
  const isSaveAsNewPrimary = activeAction === "saveAsNew";
  const isWorkspaceActionPrimary = activeAction === "workspace";

  function handleWorkspaceActionClick() {
    if (!workspaceActionLabel || !onWorkspaceAction) return;

    if (workspaceActionWarning && !showWorkspaceActionConfirm) {
      setActiveAction("workspace");
      setShowWorkspaceActionConfirm(true);
      setShowCopyNameInput(false);
      return;
    }

    setActiveAction("workspace");
    onWorkspaceAction();
  }

  return (
    <Modal
      open={isOpen}
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button
            data-testid="unsaved-changes-discard-button"
            onClick={handleDiscard}
            disabled={isSaving}
            className="bg-[rgba(255,216,216,0.40)] text-[#CC0404] border-none rounded-sm hover:bg-[rgba(255,216,216,0.60)]! hover:text-[#CC0404]!"
          >
            {discardLabel}
          </Button>
          <div className="flex items-center gap-2">
            <Button
              data-testid="unsaved-changes-save-button"
              onClick={handleSaveAsNewClick}
              loading={isSaving}
              disabled={isSaving || (showCopyNameInput && !copyName.trim())}
              className={
                isSaveAsNewPrimary
                  ? "bg-[#064C68] text-white rounded-sm hover:bg-[#04384d]! hover:text-white! border-[#064C68] hover:border-[#04384d]!"
                  : "bg-white text-[#064C68] border-[#D9D9D9] rounded-sm hover:border-[#064C68]!"
              }
            >
              {showCopyNameInput ? saveAsNewConfirmLabel : saveAsNewLabel}
            </Button>
            {workspaceActionLabel && onWorkspaceAction && (
              <Button
                data-testid="unsaved-changes-workspace-action-button"
                onClick={handleWorkspaceActionClick}
                disabled={isSaving}
                className={
                  isWorkspaceActionPrimary
                    ? "bg-[#064C68] text-white rounded-sm hover:bg-[#04384d]! hover:text-white! border-[#064C68] hover:border-[#04384d]!"
                    : "bg-white text-[#064C68] border-[#D9D9D9] rounded-sm hover:border-[#064C68]!"
                }
              >
                {showWorkspaceActionConfirm && workspaceActionConfirmLabel
                  ? workspaceActionConfirmLabel
                  : workspaceActionLabel}
              </Button>
            )}
          </div>
        </div>
      }
      closable={false}
      mask={{ closable: true }}
      keyboard
      onCancel={handleCancel}
      centered
      title={<div className="text-lg font-bold text-[#2B4752]">{title}</div>}
      width={560}
    >
      <div data-testid="unsaved-changes-modal" className="my-4">
        <p className="text-[#5F6368] text-base">{description}</p>
        {showWorkspaceActionConfirm && workspaceActionWarning && (
          <div className="mt-4 p-3 bg-[#FEF7DF] border border-[#BCA048] rounded-sm">
            <p className="text-[#283833] text-sm font-medium">
              {workspaceActionWarning}
            </p>
          </div>
        )}
        {showCopyNameInput && (
          <div className="mt-4">
            <label
              htmlFor="unsaved-changes-copy-name-input"
              className="block text-sm text-[#5F6368] mb-1"
            >
              Save as
            </label>
            <Input
              id="unsaved-changes-copy-name-input"
              data-testid="unsaved-changes-copy-name-input"
              value={copyName}
              onChange={(e) => setCopyName(e.target.value)}
              disabled={isSaving}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
